import {
  Alert,
  Autosuggest,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SegmentedControl,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import {
  BabyMachines, LotKinds, LotStatus, MoldingMaterials, PartTypes, PPartStatus,
} from '@parallel-fluidics/shared/constants';
import { lotSchema } from '@parallel-fluidics/shared/models/lots';
import createLot from 'features/lots/api/createLot';
import getPPartOption from 'features/pPart/utils/getPPartOption';
import { useFormik } from 'formik';
import { useGetProjectParts } from 'hooks/use-basicQueries';
import useProjects from 'hooks/use-projects';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

/**
 * Map the form values onto the lot fields createLot writes — one mapping
 * used by both validate and submit, so what we validate is what we save.
 */
function buildLotFields(formValues) {
  const inHouse = formValues.kind === LotKinds.INHOUSE;
  return {
    kind: formValues.kind,
    projectDocId: formValues.projectOption?.id,
    pPartDocId: formValues.partOption?.value,
    material: formValues.material.trim() || null,
    machine: inHouse ? formValues.machine : null,
    aSideTool: inHouse ? formValues.aSideTool.trim() || null : null,
    bSideTool: inHouse ? formValues.bSideTool.trim() || null : null,
    supplier: !inHouse ? formValues.supplier.trim() : null,
    orderNumber: !inHouse ? formValues.orderNumber.trim() || null : null,
    initialQty: formValues.initialQty === '' ? undefined : Number(formValues.initialQty),
  };
}

// Schema paths → form fields, for the ones whose names differ (plus
// remainingQty, which the form derives from the quantity input).
const FIELD_BY_SCHEMA_PATH = {
  projectDocId: 'projectOption',
  pPartDocId: 'partOption',
  remainingQty: 'initialQty',
};

/**
 * Creates an in-house or purchased lot (a bag of parts): pick a project
 * and Product part, enter the bag quantity, and the material prefills
 * from the part. In-house bags record which BabyPlast molded them;
 * purchased bags record the supplier and PO instead. Assembled lots
 * come from the assemble flow — not this modal.
 */
export default function NewLotModal({ open, onClose, onSuccess = () => {} }) {
  const { user } = useUserData();
  const [submitError, setSubmitError] = useState('');
  const { projectOptions, isLoading: projectsLoading } = useProjects({ isInternal: true });

  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: {
      kind: LotKinds.INHOUSE,
      machine: null,
      aSideTool: '',
      bSideTool: '',
      supplier: '',
      orderNumber: '',
      projectOption: null,
      partOption: null,
      initialQty: '',
      material: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validate: (validateValues) => {
      // Formik validates before onSubmit — clear the previous attempt's
      // submit error here so it can't linger next to validation errors.
      setSubmitError('');
      const validationErrors = {};
      const lotFields = buildLotFields(validateValues);
      try {
        // Validate the doc createLot will write, so the form rules can
        // never drift from the shared schema's. Fields the transaction
        // generates are stubbed with valid values.
        lotSchema.validateSync({
          ...lotFields,
          code: 'L-00000000-000',
          remainingQty: lotFields.initialQty,
          status: LotStatus.AWAITING_INSPECTION,
          _createdBy: { uid: user.id, email: user.email },
          // isCreate makes the schema require the in-house tool fields —
          // they're nullable on reads so pre-existing lots stay valid.
        }, { abortEarly: false, context: { isCreate: true } });
      } catch (validationError) {
        validationError.inner.forEach((issue) => {
          const field = FIELD_BY_SCHEMA_PATH[issue.path] || issue.path;
          if (!validationErrors[field]) validationErrors[field] = issue.message;
        });
      }
      return validationErrors;
    },
    onSubmit: async (submitValues) => {
      try {
        const { id, code } = await createLot({
          ...buildLotFields(submitValues),
          user: { uid: user.id, email: user.email },
        });
        toast.success(`Lot ${code} created`);
        // Close before onSuccess — the success handler navigates away, and
        // closing after would set state on the unmounted page.
        onClose();
        onSuccess({ id, code });
      } catch (error) {
        setSubmitError(error.message);
      }
    },
  });

  const { data: parts, isLoading: partsLoading } = useGetProjectParts({
    projectDocId: values.projectOption?.id,
    // Lots are bags of consumables — reusable tooling can't be bagged.
    excludeTypes: [PartTypes.TOOL, PartTypes.PISTON],
    status: PPartStatus.ACTIVE,
    disabled: !values.projectOption,
  });

  const partOptions = useMemo(() => (parts || []).map((part) => ({
    ...getPPartOption(part),
    material: part.material || '',
  })), [parts]);

  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      header="New lot"
      footer={(
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
              Create lot
            </Button>
          </SpaceBetween>
        </Box>
      )}
    >
      <SpaceBetween direction="vertical" size="m">
        {submitError ? <Alert type="error">{submitError}</Alert> : null}
        <FormField label="Kind">
          <SegmentedControl
            selectedId={values.kind}
            options={[
              { text: LotKinds.INHOUSE, id: LotKinds.INHOUSE },
              { text: LotKinds.PURCHASED, id: LotKinds.PURCHASED },
            ]}
            onChange={({ detail }) => setFieldValue('kind', detail.selectedId)}
          />
        </FormField>
        {values.kind === LotKinds.INHOUSE && (
          <FormField label="Machine" errorText={errors.machine}>
            <SegmentedControl
              selectedId={values.machine}
              options={Object.values(BabyMachines).map((machine) => ({
                text: machine, id: machine,
              }))}
              onChange={({ detail }) => setFieldValue('machine', detail.selectedId)}
            />
          </FormField>
        )}
        {values.kind === LotKinds.PURCHASED && (
          <>
            <FormField label="Supplier" errorText={errors.supplier}>
              <Input
                value={values.supplier}
                onChange={({ detail }) => setFieldValue('supplier', detail.value)}
              />
            </FormField>
            <FormField label="Order number" description="PO number, order confirmation — whatever identifies the order." errorText={errors.orderNumber}>
              <Input
                value={values.orderNumber}
                onChange={({ detail }) => setFieldValue('orderNumber', detail.value)}
              />
            </FormField>
          </>
        )}
        <FormField label="Internal Project" errorText={errors.projectOption}>
          <Select
            selectedOption={values.projectOption}
            options={projectOptions}
            statusType={projectsLoading ? 'loading' : 'finished'}
            filteringType="auto"
            placeholder="Select a project"
            onChange={({ detail }) => {
              setFieldValue('projectOption', detail.selectedOption);
              setFieldValue('partOption', null);
              setFieldValue('material', '');
            }}
          />
        </FormField>
        <FormField label="Part" errorText={errors.partOption}>
          <Select
            selectedOption={values.partOption}
            options={partOptions}
            statusType={partsLoading && values.projectOption ? 'loading' : 'finished'}
            filteringType="auto"
            placeholder="Select a part"
            disabled={!values.projectOption}
            onChange={({ detail }) => {
              setFieldValue('partOption', detail.selectedOption);
              setFieldValue('material', detail.selectedOption.material || '');
            }}
          />
        </FormField>
        <FormField label="Quantity" errorText={errors.initialQty}>
          <Input
            type="number"
            value={values.initialQty}
            onChange={({ detail }) => setFieldValue('initialQty', detail.value)}
          />
        </FormField>
        <FormField label="Material" description="Prefilled from the part — shown on the label.">
          <Autosuggest
            value={values.material}
            onChange={({ detail }) => setFieldValue('material', detail.value)}
            options={Object.values(MoldingMaterials).map((material) => ({ value: material }))}
            placeholder="Type or select a value"
            empty="No options found"
          />
        </FormField>
        {values.kind === LotKinds.INHOUSE && (
          <>
            <FormField label="A side tool" errorText={errors.aSideTool}>
              <Input
                value={values.aSideTool}
                onChange={({ detail }) => setFieldValue('aSideTool', detail.value)}
              />
            </FormField>
            <FormField label="B side tool" errorText={errors.bSideTool}>
              <Input
                value={values.bSideTool}
                onChange={({ detail }) => setFieldValue('bSideTool', detail.value)}
              />
            </FormField>
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
}

NewLotModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
